import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Facebook, Instagram, Phone } from "lucide-react";
import CategoryNav from "./CategoryNav";
// InstagramWidget removed — only on home page
import CategoryHero from "./CategoryHero";
import { useManagedCollection, slugify } from "../admin/useManagedCollection";
import { AdminItem, AdminAddBar } from "../admin/AdminControls";
import type { FieldDef } from "../admin/AdminFieldModal";
import NewsTicker from './NewsTicker';
import SocialLinks, { SOCIAL_FIELDS } from './SocialLinks';

// The platform datatable declares this multiselect as a `string` column, so a
// saved array comes back comma-joined ("Dance Schools,Choreographers") rather
// than as an array. Split it back apart — an `Array.isArray` check fails on a
// string, and a plain `===` compare can never match a multi-value pick.
function toTypeArray(v: unknown): string[] {
  if (Array.isArray(v)) return v.map((x) => String(x).trim()).filter(Boolean);
  if (typeof v === "string") return v.split(",").map((x) => x.trim()).filter(Boolean);
  return [];
}


const MAKEUP_HAIR_TYPES = [
  { name: "Makeup", image: "https://images.unsplash.com/photo-1512496015851-a90fb38ba796?w=300&h=300&fit=crop" },
  { name: "Hair", image: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=300&h=300&fit=crop" },
  { name: "Draping", image: "https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-12/51eb47ca8043.jpeg" }, // saree draping — same icon as "Clothing" on Shopping page
  { name: "Henna", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783526521368-rv74nm.png" },
];

const MAKEUP_HAIR_FIELDS: FieldDef[] = [
  { key: "name", label: "Name", required: true },
  { key: "city", label: "City", placeholder: "Dallas" },
  { key: "address", label: "Address", type: "textarea" },
  { key: "phone", label: "Phone" },
  { key: "website", label: "Website URL", type: "url", placeholder: "https://…" },
  { key: "contactUrl", label: "Contact URL (Instagram/Facebook)", type: "url" },
  { key: "image", label: "Image URL", type: "image", placeholder: "https://…" },
  { key: "type", label: "Types", type: "multiselect", options: MAKEUP_HAIR_TYPES.map((t) => t.name), help: "Select all services this business offers." },
  { key: "description", label: "Description", type: "textarea", placeholder: "Short description of this business" },
  { key: "featuredInstagram", label: "Featured Instagram Post URL", type: "url", placeholder: "https://instagram.com/p/…" },
  ...SOCIAL_FIELDS,
];

export interface MakeupArtist {
  name: string;
  title?: string;
  city: string;
  address: string;
  website: string;
  phone: string;
  image: string;
  imageFit?: "cover" | "contain";
  contactUrl: string;
  instagram?: string;
  facebook?: string;
  type?: string | string[];
  description?: string;
  /** Specific post/reel the "Featured by TastebudsByAnubhi" badge's Instagram icon links to. */
  featuredInstagram?: string;
}

// Cards featured by TastebudsByAnubhi — matched by name so it survives
// managed-collection re-saves the same way Restaurants/Desserts/Grocery/Kids do.
const FEATURED_ARTIST_NAMES = new Set(["henna stain by nikita", "glambynisha_patel", "makeup by pragnya", "makeup by pragnya & saree draping", "elegant beauty services"]);
function isFeaturedArtist(a: { name?: string }) {
  return FEATURED_ARTIST_NAMES.has(String(a?.name || "").trim().toLowerCase());
}

export const MAKEUP_ARTISTS: MakeupArtist[] = [
  {
    name: "makeoversbyminii",
    city: "Dallas",
    address: "Address coming soon",
    website: "",
    phone: "Coming soon",
    image: "https://images.unsplash.com/photo-1487412912498-0447578fcca8?w=600&h=400&fit=crop",
    contactUrl: "https://www.instagram.com/makeoversbyminii/",
    type: "Makeup",
  },
  {
    name: "vanitapatelmakeovers",
    city: "Dallas",
    address: "Address coming soon",
    website: "",
    phone: "Coming soon",
    image: "https://images.unsplash.com/photo-1457972729786-0411a3b2b626?w=600&h=400&fit=crop",
    contactUrl: "https://www.instagram.com/vanitapatelmakeovers/",
    type: "Makeup",
  },
  {
    name: "glambynisha_patel",
    city: "Dallas",
    address: "Address coming soon",
    website: "",
    phone: "Coming soon",
    image: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=600&h=400&fit=crop",
    contactUrl: "https://www.instagram.com/glambynisha_patel/",
    type: "Hair",
    featuredInstagram: "https://www.instagram.com/p/DIg6lUQNj6j/",
  },
  {
    name: "Anamika Vegad MUA",
    city: "Dallas",
    address: "Address coming soon",
    website: "",
    phone: "Coming soon",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784444955373-y9lvgl.png",
    contactUrl: "https://www.instagram.com/anamikavegadmua/",
    type: "Makeup",
  },
  {
    name: "MakeupandhairbyPOOJA",
    city: "Dallas",
    address: "Address coming soon",
    website: "",
    phone: "Coming soon",
    image: "https://images.unsplash.com/photo-1512496015851-a90fb38ba796?w=600&h=400&fit=crop",
    contactUrl: "https://www.instagram.com/makeupandhairbypooja/",
    type: ["Makeup", "Hair"],
  },
  {
    name: "Elegant Beauty Services",
    city: "Dallas",
    address: "DFW Area",
    website: "",
    phone: "Coming soon",
    image: "https://images.unsplash.com/photo-1487412912498-0447578fcca8?w=600&h=400&fit=crop",
    contactUrl: "https://www.facebook.com/groups/467166136634442",
    type: ["Makeup", "Hair"],
    featuredInstagram: "https://www.instagram.com/p/DJzuhykyV8g/",
  },
  {
    name: "Makeup by Pragnya & Saree Draping",
    city: "McKinney",
    address: "6600 McKinney Ranch Pkwy, McKinney, TX 75070",
    website: "",
    phone: "(945) 268-5768",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-08/822a89b91d10.jpeg",
    imageFit: "contain",
    contactUrl: "https://www.instagram.com/makeup_by_pragnya?igsh=NmhoNGp4MG51N2ls&utm_source=qr",
    type: ["Makeup", "Hair", "Draping"],
    featuredInstagram: "https://www.instagram.com/reels/DOG3FygDavj/",
  },
  {
    name: "Blush Me Pink, Akshi Bhatia",
    city: "Dallas",
    address: "Address coming soon",
    website: "",
    phone: "Coming soon",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783711715652-48sgjv.png",
    imageFit: "contain",
    contactUrl: "https://www.instagram.com/msblushmepink",
    type: ["Makeup", "Hair"],
  },
  {
    name: "Saree draping by Shreya Desai",
    city: "Dallas",
    address: "Address coming soon",
    website: "",
    phone: "(214) 263-4764",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783712501225-p172k.png",
    imageFit: "contain",
    contactUrl: "",
    type: "Draping",
  },
  {
    name: "Johal Henna Beauty",
    city: "Dallas",
    address: "Address coming soon",
    website: "",
    phone: "Coming soon",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783526675012-a02klt.png",
    contactUrl: "https://www.instagram.com/johalhennabeauty",
    type: ["Makeup", "Hair", "Henna"],
  },
  {
    name: "Henna Stain By Nikita",
    city: "Dallas",
    address: "Address coming soon",
    website: "",
    phone: "Coming soon",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783699195401-9grtca.png",
    imageFit: "contain",
    contactUrl: "https://www.instagram.com/hennastainby_nikita_dallas/",
    type: "Henna",
    featuredInstagram: "https://www.instagram.com/p/Cvczuj3g8hy/",
  },
  {
    name: "Glow and Blush by Mina",
    city: "Lewisville",
    address: "Address coming soon",
    website: "",
    phone: "Coming soon",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-12/4a49389055e2.jpeg",
    contactUrl: "https://www.instagram.com/glowandblushbymina",
    type: "Makeup",
  },
  {
    name: "Dallas Henna Artist Rimple",
    title: "Bridal, Festival, 100% organic",
    city: "Dallas",
    address: "1517 Autumn Chase Square, Bedford, 76022",
    website: "",
    phone: "Coming soon",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-15/fe0e3448b9c8.jpeg",
    contactUrl: "https://instagram.com/dallas_hennaartist_rimple",
    type: "Henna",
    description:
      "I am the artist behind Dallas Hennaartist Rimple, a luxury henna studio based in Bedford serving the entire Dallas-Fort Worth area. I specialize in bridal, bridal party, and festival henna using 100% natural, organic henna paste made fresh for every appointment for a deep, long-lasting stain. With over years of experience, I create everything from intricate traditional bridal designs to minimal, modern styles. Services include bridal trials, on-site bridal henna for weddings, sangeet/mehndi nights, private appointments, corporate events, birthdays, and baby showers. I offer both studio and on-location travel. My goal is to make every client feel celebrated with clean, detailed art and a calm, hygienic, and joyful experience",
  },
  {
    name: "Makeoverbykeny",
    title: "Love to create Beauty",
    city: "Dallas",
    address: "Address coming soon",
    website: "",
    phone: "Coming soon",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-12/96c698402b0d.jpeg",
    contactUrl: "https://www.instagram.com/makeoverbykeny_dallasartist",
    type: "Makeup",
  },
  {
    name: "Mehendi Nirmala Artistry",
    city: "Dallas",
    address: "Address coming soon",
    website: "",
    phone: "682-240-2541",
    image: "https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=600&h=400&fit=crop",
    contactUrl: "#",
    instagram: "mehendinirmala",
    type: "Henna",
    description:
      "Welcome to Mehendi Nirmala Artistry With over 20 years of professional experience, I specialize in creating beautiful, intricate henna designs that make every celebration truly memorable. Whether it's your wedding, engagement, baby shower, festival, birthday, or any special occasion, I am dedicated to adding elegance and joy through the timeless art of henna. I use 100% homemade organic henna paste, carefully prepared using natural ingredients without harsh chemicals. This ensures a rich, long-lasting dark stain while being gentle on the skin. For brides, the deep, beautiful stain symbolizes love, happiness, and the beginning of a wonderful new journey with your soulmate. Every design is customized to reflect your style, personality, and the significance of your special day. With extensive experience at weddings, festivals, and large community events, I am known for my speed, precision, and ability to efficiently manage large crowds without compromising on quality. Whether you need henna for an intimate gathering or a large celebration, you can count on a smooth, professional, and enjoyable experience. Let Mehendi Nirmala Artistry make your special moments even more beautiful with elegant designs, natural organic henna, and exceptional service.",
  },
  {
    name: "STITCH & DRAPE",
    title: "Sewing Alterations & Saree Draping",
    city: "Plano",
    address: "Address coming soon",
    website: "",
    phone: "214-970-4568",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784424318359-rgn3ahc.jpg",
    imageFit: "contain",
    contactUrl: "",
    type: "Draping",
  },
  {
    name: "Sumaira Henna Arts Makeup & Hair Stylist",
    city: "Dallas",
    address: "Address coming soon",
    website: "",
    phone: "Coming soon",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-09-23/8394d6d2d09a.jpeg",
    contactUrl: "https://www.instagram.com/sumaira.lakhani.946?stkn=NzEzeWpuN3J1Y2U5&utm_source=qr",
    type: ["Makeup", "Hair", "Henna"],
    description:
      "Professional Henna • Makeup • Hair Styling • Face Painting • Glitter Tattoos Creating beautiful looks and memorable moments for weddings, celebrations and special events",
  },
];

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1487412912498-0447578fcca8?w=600&h=400&fit=crop";

export function MakeupArtistCard({ artist, index, isFeatured }: { artist: MakeupArtist; index: number; isFeatured?: boolean }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <div className="w-full h-[110px] relative overflow-hidden bg-gray-50">
        <img
          src={artist.image}
          alt={artist.name}
          className={artist.imageFit === "contain" ? "w-full h-full object-contain" : "w-full h-full object-cover"}
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
          }}
        />
      </div>
      <div className="p-2.5 pb-1 flex-1">
        <h3 className="text-[12px] font-bold text-gray-900 leading-tight mb-0.5 line-clamp-2">
          <Link to={`/BeautyServices/${slugify(artist.name)}`} className="hover:text-[#082f6d] hover:underline">
            {artist.name}
          </Link>
        </h3>
        {artist.title && (
          <p className="text-[10px] text-gray-500 leading-snug mb-1 line-clamp-2">
            {artist.title}
          </p>
        )}
        <div className="flex items-start gap-1 mb-0.5">
          <span className="text-[10px] text-gray-500 font-medium leading-snug">{artist.city}</span>
        </div>
        {isFeatured && (
          <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold tracking-wide">
            <span className="text-[#51ba4f]">Featured by TastebudsByAnubhi</span>
            {artist.featuredInstagram && (
              <a
                href={artist.featuredInstagram}
                target="_blank"
                rel="noopener noreferrer"
                title="Instagram"
                onClick={(e) => e.stopPropagation()}
                className="flex items-center justify-center rounded-full text-white hover:scale-110 transition-transform duration-200 bg-gradient-to-br from-pink-500 to-orange-400 shrink-0"
                style={{ width: 14, height: 14 }}
              >
                <Instagram className="w-2 h-2" />
              </a>
            )}
          </div>
        )}
      </div>
      <div className="flex gap-2 px-2.5 mt-2 w-full">
        {artist.phone && artist.phone !== "Coming soon" && (
          <a
            href={`tel:${artist.phone.replace(/[^\d+]/g, "")}`}
            className="flex-1 flex items-center justify-center gap-1 text-[9px] font-semibold text-white bg-[#51ba4f] hover:bg-[#459f43] py-1 rounded-md transition-colors duration-200"
          >
            <Phone className="w-2.5 h-2.5" /> Call
          </a>
        )}
        {artist.instagram && (
          <a
            href={`https://instagram.com/${artist.instagram}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-1 text-[9px] font-semibold text-white bg-pink-600 hover:bg-pink-700 py-1 rounded-md transition-colors duration-200"
          >
            <Instagram className="w-2.5 h-2.5" /> IG
          </a>
        )}
      </div>
      <div className="px-2.5 pb-1 pt-1">
        <SocialLinks item={artist} px={18} />
      </div>
      <div className="px-2.5 pb-2.5 pt-0.5 w-full">
        {(() => {
          // The big button always points at contactUrl, mirroring the
          // Decorators page pattern — cosmetic Facebook/Instagram styling
          // is just based on which domain contactUrl happens to be.
          const linkUrl = artist.contactUrl && artist.contactUrl !== "#" ? artist.contactUrl : null;
          const isFacebookContact = !!linkUrl && linkUrl.includes("facebook.com");

          if (!linkUrl) {
            return null;
          }

          if (isFacebookContact) {
            return (
              <a
                href={linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-1 text-white text-[9px] font-semibold py-1 rounded-md transition-opacity duration-200 hover:opacity-90"
                style={{ backgroundColor: "#1877F2" }}
              >
                <Facebook className="w-2.5 h-2.5" /> Facebook
              </a>
            );
          }

          return (
            <a
              href={linkUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-1 text-white text-[9px] font-semibold py-1 rounded-md transition-opacity duration-200 hover:opacity-90"
              style={{
                background:
                  "radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)",
              }}
            >
              <Instagram className="w-2.5 h-2.5" /> Instagram
            </a>
          );
        })()}
      </div>
    </motion.div>
  );
}

export default function MakeupHair() {
  const [query, setQuery] = useState("");
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const mc = useManagedCollection<any>("makeup_hair", MAKEUP_ARTISTS, (a, i) => slugify(a.name || String(i)));

  const filtered = mc.items
    .filter(
      (a) =>
        (query.trim() === "" ||
          a.name.toLowerCase().includes(query.trim().toLowerCase())) &&
        (!selectedType ||
          toTypeArray(a.type).includes(selectedType))
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const featuredArtists = filtered.filter((a) => isFeaturedArtist(a));
  const nonFeaturedArtists = filtered.filter((a) => !isFeaturedArtist(a));

  return (
    <div className="min-h-screen bg-white">
      <CategoryHero
        placeholder="Search makeup & hair artists…"
        query={query}
        setQuery={setQuery}
        hideLocation
        backgroundImage="https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-25/e3273d7c6fec.png"
      />
      <NewsTicker />

      <CategoryNav />

      {/* Explore by Type */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 pt-6 pb-4">
          <p
            className="text-[11px] font-bold text-gray-400 uppercase mb-4"
            style={{ fontFamily: "'Poppins', sans-serif", letterSpacing: "0.12em" }}
          >
            Explore by Type
          </p>
          <div className="flex gap-6 overflow-x-auto pb-2" style={{ scrollbarWidth: "none" }}>
            {MAKEUP_HAIR_TYPES.map((t) => {
              const isActive = selectedType === t.name;
              return (
                <button
                  key={t.name}
                  onClick={() => setSelectedType(isActive ? null : t.name)}
                  className="flex flex-col items-center gap-2 shrink-0 group"
                >
                  <div
                    className={`w-[68px] h-[68px] rounded-full p-[2px] transition-transform duration-200 shadow-sm ${
                      isActive
                        ? "bg-gradient-to-tr from-[#51ba4f] to-[#3f9a3d] scale-110"
                        : "bg-gradient-to-tr from-[#082f6d] to-[#51ba4f] group-hover:scale-105"
                    }`}
                  >
                    <div className="w-full h-full rounded-full border-[2.5px] border-white overflow-hidden bg-white">
                      <img src={t.image} alt={t.name} className="w-full h-full object-cover" />
                    </div>
                  </div>
                  <span
                    className={`text-[12px] font-medium transition-colors ${
                      isActive ? "text-[#51ba4f] font-semibold" : "text-gray-700"
                    }`}
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                  >
                    {t.name}
                  </span>
                </button>
              );
            })}
            {selectedType && (
              <button
                onClick={() => setSelectedType(null)}
                className="flex items-center gap-1.5 rounded-full border border-dashed border-gray-300 text-gray-500 hover:text-[#51ba4f] hover:border-[#51ba4f] hover:bg-[#f0fdf4] whitespace-nowrap transition-colors duration-150 shrink-0 self-center"
                style={{ fontFamily: "'Poppins', sans-serif", height: "35px", padding: "0 13px", fontSize: "12.5px", fontWeight: 600 }}
              >
                ✕ Clear Filter
              </button>
            )}
          </div>
        </div>
      </div>

      <section
        className="bg-gray-50/40 py-8"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex flex-col lg:flex-row gap-6">
          {/* LEFT: artists */}
          <div className="flex-1 min-w-0">
            <h2 className="text-[16px] md:text-[18px] font-bold text-gray-900 mb-1">
              Beauty Services Artists in Dallas–DFW
            </h2>
            <p className="text-[13px] text-gray-500 mb-5">
              Top South Asian makeup and hair artists for your special occasions
            </p>
            <AdminAddBar mc={mc} fields={MAKEUP_HAIR_FIELDS} label="Add Artist" />

            {/* Featured by TastebudsByAnubhi */}
            {featuredArtists.length > 0 && (
              <div className="mb-8">
                <h3 className="text-[14px] sm:text-[16px] font-bold text-green-700 mb-3" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  Featured by TastebudsByAnubhi
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {featuredArtists.map((a, i) => (
                    <AdminItem key={a._key} mc={mc} item={a} fields={MAKEUP_HAIR_FIELDS} title="Artist">
                      <MakeupArtistCard artist={a as any} index={i} isFeatured />
                    </AdminItem>
                  ))}
                </div>
              </div>
            )}

            {nonFeaturedArtists.length > 0 ? (
              <div>
                {featuredArtists.length > 0 && (
                  <h3 className="text-[14px] sm:text-[16px] font-bold text-gray-700 mb-3" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    Explore More Artists
                  </h3>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {nonFeaturedArtists.map((a, i) => (
                    <AdminItem key={a._key} mc={mc} item={a} fields={MAKEUP_HAIR_FIELDS} title="Artist">
                      <MakeupArtistCard artist={a as any} index={i} />
                    </AdminItem>
                  ))}
                </div>
              </div>
            ) : featuredArtists.length === 0 ? (
              <div className="bg-white border border-dashed border-gray-200 rounded-2xl p-8 text-center text-[13px] text-gray-500">
                No artists match your search.
              </div>
            ) : null}
          </div>

          {/* Instagram widget removed — only on home page */}
        </div>
      </section>
    </div>
  );
}
